<script setup lang="ts">
// Create task's approvals (#1753): one row per tool on the form, each set to Ask me first, Let it run or Ask the lead.
// The choices are the kit's segmented RadioGroup; this list states no colour, border, fill or radius of its own.

import { RadioGroup, RadioGroupItem } from '@zerohuman/ui'
import { computed } from 'vue'
import { APPROVAL_CHOICES, type Approval, approvalOf, type GateRow, isApproval } from '@/lib/taskApprovals'

const props = defineProps<{
    /** The tools on the form, in the order the tools picker shows them. */
    tools: readonly string[]
    /** The form's gate rows: what the list reads, and what the raw Gates editor under Advanced shows. */
    gates: readonly GateRow[]
    /** How the tools picker names a tool, so a row says what its card says. */
    label: (tool: string) => string
    /** Tools the tools picker says must be gated: fixed on Ask me first. */
    mustGate: readonly string[]
    /** Portal Advanced mode: the raw tool name shows beside its label. */
    showToolNames: boolean
}>()

const emit = defineEmits<{ choose: [tool: string, choice: Approval] }>()

const rows = computed(() =>
    props.tools.map(tool => {
        const label = props.label(tool)
        const state = approvalOf(props.gates, tool)
        const mustGate = props.mustGate.includes(tool)
        const disabled: Record<Approval, boolean> = {
            ceo: false,
            run: mustGate,
            // A task has no team of its own: Ask the lead waits for the lead of the running member's team, or the COO.
            lead: mustGate,
        }
        return {
            tool,
            label,
            rawName: props.showToolNames && label !== tool ? tool : null,
            state,
            mustGate,
            disabled,
        }
    }),
)

/** A click on the choice already selected changes nothing: a CEO row the picker added stays as it was. */
function choose(tool: string, current: string, next: unknown): void {
    if (next !== current && isApproval(next)) {
        emit('choose', tool, next)
    }
}
</script>

<template>
	<section class="space-y-2" data-testid="task-approvals">
		<p class="text-sm font-medium">Approvals</p>
		<p class="text-muted-foreground text-sm">Whether a tool waits for someone before it runs.</p>
		<ul class="space-y-3">
			<li
				v-for="row in rows"
				:key="row.tool"
				data-testid="task-approval-row"
				class="flex flex-wrap items-center justify-between gap-2"
			>
				<div class="min-w-0 space-y-0.5">
					<p class="break-all text-sm">
						{{ row.label }}
						<span v-if="row.rawName" class="text-muted-foreground font-mono text-xs">{{ row.rawName }}</span>
					</p>
					<p v-if="row.mustGate" class="text-muted-foreground text-xs">This tool must be approved by you before it runs.</p>
					<p v-if="row.state.choice === 'other'" class="text-muted-foreground text-xs">
						Decided by {{ row.state.owner }}, as set under Advanced. Choose one to change it.
					</p>
				</div>
				<RadioGroup
					:model-value="row.state.choice"
					:aria-label="`Approval for ${row.label}`"
					@update:model-value="choose(row.tool, row.state.choice, $event)"
				>
					<RadioGroupItem
						v-for="choice in APPROVAL_CHOICES"
						:key="choice.value"
						:value="choice.value"
						:disabled="row.disabled[choice.value]"
					>
						{{ choice.label }}
					</RadioGroupItem>
				</RadioGroup>
			</li>
		</ul>
	</section>
</template>
